<i18n>
{
  "en": {
    "save": "Save",
    "cancel": "Cancel",
    "reset": "Reset",
    "show_advanced_config": "Show advanced config",
    "hide_advanced_config": "Hide advanced config",
    "please_check_value": "Please check all values",
    "current_value": "Current value",
    "yes": "Yes",
    "no": "No"
  },
  "zhHant": {
    "save": "存檔",
    "cancel": "不存檔退出",
    "reset": "重置",
    "show_advanced_config": "顯示進階設定",
    "hide_advanced_config": "隱藏進階設定",
    "please_check_value": "請檢查所有設定",
    "current_value": "現在設定",
    "yes": "是",
    "no": "否"
  },
  "ja": {
    "save": "セーブ",
    "cancel": "キャンセル",
    "reset": "リセット",
    "show_advanced_config": "詳細設定を開く",
    "hide_advanced_config": "詳細設定を閉じる",
    "please_check_value": "設定を確認してください",
    "current_value": "現在の設定",
    "yes": "はい",
    "no": "いいえ"
  },
  "ko": {
    "save": "저장",
    "cancel": "취소",
    "reset": "초기화",
    "show_advanced_config": "매크로 단계별 설정을 표시합니다.",
    "hide_advanced_config": "매크로 단계별 설정을 숨깁니다.",
    "please_check_value": "모든 설정을 확인하십시오.",
    "current_value": "현재 설정",
    "yes": "네",
    "no": "아니요"
  }
}
</i18n>

<template>
  <div>
    <v-card-text>
      <v-flex v-show="showAdvancedConfig || item.basic" xs12 sm12 md12 v-for="(item, key) in localTaskConfig"
        :key="key">
        <div v-if="!item.parent" class="mt-2 font-weight-bold">
          {{ item.title[$i18n.locale] }}
        </div>
        <v-divider v-if="!item.parent" class="mb-2 mt-0"></v-divider>
        <v-switch v-if="typeof item.value === 'boolean' || item.type === 'bool'" v-model="item.value"
          :disabled="isItemDisabled(item)"
          :label="`${item.title[$i18n.locale]}: ${item.value ? $i18n.t('yes') : $i18n.t('no')}`">
          <template v-slot:prepend v-if="item.help">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on">mdi-help-circle-outline</v-icon>
              </template>
              {{ item.help[$i18n.locale] }}
            </v-tooltip>
          </template>
        </v-switch>

        <v-select v-else-if="item.type === 'selection'" v-model="item.value"
          :items="translatedSelectOptions(item.options)" :disabled="isItemDisabled(item)"
          :label="`${item.title[$i18n.locale]} ${$t('current_value')}: ${getTranslatedSelectLabel(item)}`">
          <template v-slot:append-outer v-if="item.help">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on">mdi-help-circle-outline</v-icon>
              </template>
              {{ item.help[$i18n.locale] }}
            </v-tooltip>
          </template>
        </v-select>

        <v-text-field v-else-if="item.type == 'number'" v-model="item.value" v-show="item.hidden !== true"
          :type="item.type !== undefined ? item.type : 'text'" :rules="bindRules(item.rules, item)"
          :validate-on-blur="true" :disabled="isItemDisabled(item)"
          :label="`${item.title[$i18n.locale]} ${$t('current_value')}: ${item.currentValue}`"
          :clearable="item.clearable" :max="item.max || undefined" :min="item.min || undefined">
          <template v-slot:append-outer v-if="item.help">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on">mdi-help-circle-outline</v-icon>
              </template>
              {{ item.help[$i18n.locale] }}
            </v-tooltip>
          </template>
        </v-text-field>

        <v-text-field v-else v-model="item.value" v-show="item.hidden !== true"
          :type="item.type !== undefined ? item.type : 'text'" :rules="bindRules(item.rules, item)"
          :validate-on-blur="true" :disabled="isItemDisabled(item)"
          :label="`${item.title[$i18n.locale]} ${$t('current_value')}: ${item.currentValue}`"
          :clearable="item.clearable">
          <template v-slot:append-outer v-if="item.help">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on">mdi-help-circle-outline</v-icon>
              </template>
              {{ item.help[$i18n.locale] }}
            </v-tooltip>
          </template>
        </v-text-field>
      </v-flex>
      <v-btn v-if="advancedConfigBtn" @click="showAdvancedConfig = !showAdvancedConfig" block>{{
        showAdvancedConfig ? $t('hide_advanced_config') : $t('show_advanced_config')
      }}</v-btn>
    </v-card-text>
    <v-card-actions>
      <v-btn color="grey" @click="reset" outline>{{ $t('reset') }}</v-btn>
      <v-spacer />
      <v-btn color="red" outline @click="back">{{ $t('cancel') }}</v-btn>
      <v-btn color="green" @click="save" :disabled="isLoading" :loading="isLoading">{{ $t('save') }}</v-btn>
    </v-card-actions>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';

export default {
  name: 'Config',
  props: {
    localTaskConfig: {
      type: Object,
      required: true,
    },
    licenseType: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    advancedConfigBtn: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showAdvancedConfig: false,
    };
  },
  created() { },
  computed: {},
  watch: {},
  methods: {
    isEmpty(x) {
      return isEmpty(x);
    },
    isItemDisabled(item) {
      return item.parent && this.localTaskConfig[item.parent].value === false;
    },
    back() {
      this.$router.push({ name: 'home' });
    },
    reset() {
      for (const key in this.localTaskConfig) {
        this.localTaskConfig[key].value = this.localTaskConfig[key].currentValue;
      }
    },
    save() {
      const errors = this.checkValues();
      if (errors.length === 0) {
        this.$emit('save', this.localTaskConfig);
      } else {
        alert(this.$i18n.t('please_check_value'));
        return;
      }
      // extract values
      const configToPost = {};
      for (const key in this.localTaskConfig) {
        // check is number type
        if (typeof this.localTaskConfig[key].value === 'number') {
          configToPost[key] = +this.localTaskConfig[key].value;
        } else {
          configToPost[key] = this.localTaskConfig[key].value;
        }
      }
      this.$emit('onSave', configToPost);
    },
    checkValues() {
      const errors = [];
      Object.keys(this.localTaskConfig).forEach(key => {
        const item = this.localTaskConfig[key];
        if (item.rules) {
          const rules = this.bindRules(item.rules, item);
          rules.forEach(rule => {
            const error = rule(item.value);
            if (error !== true) {
              errors.push(error);
            }
          });
        }
      });
      return errors;
    },
    bindRules(rules, item) {
      if (isEmpty(rules)) {
        return [];
      }
      return rules.map(rule => rule.bind({ item, $i18n: this.$i18n }));
    },
    translatedSelectOptions(options) {
      return options.map(option => ({
        value: option.value,
        text: option.text[this.$i18n.locale],
      }));
    },
    getTranslatedSelectLabel(item) {
      const option = item.options.find(o => o.value === item.currentValue);
      return option ? option.text[this.$i18n.locale] : item.currentValue;
    },
  },
};
</script>
